import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { tierUnitPrice } from "../lib/pricing";
import { useAuth } from "./AuthContext";
import api from "../api";

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const KEY = "psl_cart_v2";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const snapshotTimer = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  /**
   * product: full product doc (id, slug, name, image)
   * variant: variant doc (size, sku, price, tiers[], wholesale_price)
   * qty: number
   * opts.wholesale: if true, this line uses the wholesale_price for unit math
   *   instead of retail tiers. Mixing wholesale + retail in one cart is blocked
   *   at the page level — the cart enforces "wholesale mode" if any item is.
   */
  const addItem = (product, variant, qty = 1, opts = {}) => {
    const isWholesale = !!opts.wholesale;
    setItems((cur) => {
      // Block mixing: if cart already has items of a different mode, clear it
      const existingMode = cur.length ? !!cur[0].is_wholesale : null;
      const next = existingMode !== null && existingMode !== isWholesale ? [] : cur;
      const idx = next.findIndex(
        (i) => i.product_id === product.id && i.variant === variant.size
      );
      if (idx >= 0) {
        const copy = [...next];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
        return copy;
      }
      return [
        ...next,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          image: product.image,
          variant: variant.size,
          sku: variant.sku || null,
          list_price: Number(variant.price || 0),
          tiers: variant.tiers || [],
          wholesale_price: isWholesale ? Number(variant.wholesale_price || 0) : null,
          is_wholesale: isWholesale,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const updateQty = (product_id, variant, qty) => {
    setItems((cur) =>
      cur
        .map((i) =>
          i.product_id === product_id && i.variant === variant
            ? { ...i, quantity: qty }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (product_id, variant) => {
    setItems((cur) =>
      cur.filter((i) => !(i.product_id === product_id && i.variant === variant))
    );
  };

  const clear = () => setItems([]);

  const enriched = items.map((it) => {
    let unit;
    if (it.is_wholesale) {
      unit = Number(it.wholesale_price || 0);
    } else {
      const variantLike = { price: it.list_price, tiers: it.tiers };
      unit = tierUnitPrice(variantLike, it.quantity);
    }
    const line_total = Math.round(unit * it.quantity * 100) / 100;
    const list_total = Math.round(it.list_price * it.quantity * 100) / 100;
    return {
      ...it,
      unit_price: unit,
      line_total,
      list_total,
      tier_savings: Math.max(0, Math.round((list_total - line_total) * 100) / 100),
    };
  });

  const subtotal = enriched.reduce((s, i) => s + i.line_total, 0);
  const listTotal = enriched.reduce((s, i) => s + i.list_total, 0);
  const tierSavings = Math.max(0, Math.round((listTotal - subtotal) * 100) / 100);
  const count = enriched.reduce((s, i) => s + i.quantity, 0);
  const wholesale = enriched.length > 0 && enriched.every((i) => i.is_wholesale);
  const totalKits = wholesale ? Math.floor(count / 10) : 0;

  // Debounced server-side snapshot so the abandoned-cart job can email reminders
  useEffect(() => {
    if (!user) return; // anonymous carts can't be reminded
    if (snapshotTimer.current) clearTimeout(snapshotTimer.current);
    snapshotTimer.current = setTimeout(() => {
      const payload = {
        items: enriched.map((i) => ({
          product_id: i.product_id,
          slug: i.slug,
          name: i.name,
          variant: i.variant,
          quantity: i.quantity,
          line_total: i.line_total,
        })),
      };
      api.post("/cart/snapshot", payload).catch(() => {});
    }, 2000);
    return () => snapshotTimer.current && clearTimeout(snapshotTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, user]);

  return (
    <CartCtx.Provider
      value={{
        items: enriched,
        addItem,
        updateQty,
        removeItem,
        clear,
        subtotal,
        listTotal,
        tierSavings,
        count,
        wholesale,
        totalKits,
        open,
        setOpen,
      }}
    >
      {children}
    </CartCtx.Provider>
  );
}
